/**
 * Drafts & sent (Slack, logic/drafts): my unsent drafts across conversations, kept on the server so they follow me to
 * my other devices, plus the messages I sent lately. Also the sync the composers use (ChannelViewModel): each
 * keystroke stores the draft on this device at once (works offline) and saves it to the server after a pause
 * (DRAFT_SYNC_DELAY_MS); saves that fail wait in a queue on this device and go out when the network is back.
 *
 * One per workspace (WorkspaceViewModel.drafts). Without the draft API (tests, older ports) drafts stay local.
 */
import type { Draft, Message, SaveDraftRequest } from "@bundle/contracts";
import { DRAFT_SYNC_DELAY_MS, applySaved, draftPreview, draftScope, queueSave, removeDraft, sortDrafts } from "../logic/drafts.ts";
import { messagePreview } from "../logic/messages.ts";
import { channelTitle } from "../logic/sidebar.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type DraftRow = {
  id: string;
  draft: Draft;
  /** "#design", a DM's people. */
  conversation: string;
  isDm: boolean;
  inThread: boolean;
  /** One line of the text. */
  preview: string;
};

export type SentRow = {
  id: string;
  message: Message;
  /** The message as one line of plain text (people and channels by name, formatting dropped). */
  preview: string;
  conversation: string;
  isDm: boolean;
  inThread: boolean;
};

export type DraftsViewState = {
  status: "loading" | "ready" | "failed";
  /** All of mine, most recent first. */
  drafts: Draft[];
  /** Sent tab: my latest messages, newest first (loaded on demand). */
  sent?: Message[];
  sentStatus?: "loading" | "ready" | "failed";
  offline: boolean;
};

/** Longest line of a Sent row. */
const SENT_PREVIEW_MAX = 280;

/** Storage keys shared with ChannelViewModel's local copy (`bundle.draft.<ws>.<scope>`). */
export const DRAFT_TEXT_PREFIX = "bundle.draft.";
const DRAFT_AT_PREFIX = "bundle.draftAt.";
const PENDING_PREFIX = "bundle.draftPending.";

export class DraftsViewModel extends ViewModel<DraftsViewState> {
  private readonly timers = new Map<string, () => void>();
  private readonly latest = new Map<string, SaveDraftRequest>();
  private pending: Record<string, SaveDraftRequest>;
  private flushing = false;

  constructor(private readonly ws: WorkspaceViewModel) {
    super({ status: "loading", drafts: [], offline: !ws.state.network });
    this.pending = parsePending(ws.deps.storage.get(this.pendingKey));
    let online = ws.deps.network.getSnapshot();
    this.onDispose(
      ws.deps.network.subscribe(() => {
        const now = ws.deps.network.getSnapshot();
        if (now && !online) void this.flushPending();
        online = now;
      }),
    );
    this.onDispose(() => {
      // Leaving: whatever is still waiting for its pause goes out now (or into the queue).
      for (const scope of Array.from(this.timers.keys())) this.flushScope(scope);
    });
    if (Object.keys(this.pending).length > 0) void this.flushPending();
  }

  /** Whether drafts sync with the server here. */
  get synced(): boolean {
    return this.ws.deps.api.saveDraft !== undefined && this.ws.deps.api.listDrafts !== undefined;
  }

  private get pendingKey(): string {
    return `${PENDING_PREFIX}${this.ws.wsId}`;
  }

  private atKey(scope: string): string {
    return `${DRAFT_AT_PREFIX}${this.ws.wsId}.${scope}`;
  }

  private textKey(scope: string): string {
    return `${DRAFT_TEXT_PREFIX}${this.ws.wsId}.${scope}`;
  }

  // ---------- The list ----------

  refresh(): Promise<void> {
    const list = this.ws.deps.api.listDrafts;
    if (list === undefined) {
      this.update({ status: "ready" });
      return Promise.resolve();
    }
    return list.call(this.ws.deps.api, this.ws.wsId).then(
      (drafts) => {
        if (this.isDisposed) return;
        // Saves still waiting here are newer than what the server has.
        let merged = sortDrafts(drafts);
        for (const req of Object.values(this.pending)) merged = applySaved(merged, req.channelId, req.threadRootId, this.asDraft(req, merged));
        this.update({ status: "ready", drafts: merged, offline: false });
      },
      () => {
        if (!this.isDisposed && this.state.status === "loading") this.update({ status: "failed", offline: !this.ws.state.network });
      },
    );
  }

  /** Rows for the Drafts tab (conversations I can no longer see are left out). */
  rows(): DraftRow[] {
    const cache = this.ws.state.cache;
    if (cache === undefined) return [];
    const rows: DraftRow[] = [];
    for (const d of this.state.drafts) {
      const c = cache.channels[d.channelId];
      if (c === undefined) continue;
      const isDm = c.kind === "dm" || c.kind === "group_dm";
      rows.push({ id: d.id, draft: d, conversation: isDm ? channelTitle(cache, c.id) : `#${c.name}`, isDm, inThread: d.threadRootId !== undefined, preview: draftPreview(d.text) });
    }
    return rows;
  }

  /** Deletes a draft (here and on the server); the composer opens empty. */
  delete(id: string): void {
    const d = this.state.drafts.find((x) => x.id === id);
    if (d === undefined) return;
    this.update({ drafts: removeDraft(this.state.drafts, id) });
    const scope = draftScope(d.channelId, d.threadRootId);
    this.ws.deps.storage.remove(this.textKey(scope));
    this.ws.deps.storage.set(this.atKey(scope), String(this.ws.deps.clock.now()));
    this.cancelTimer(scope);
    this.latest.delete(scope);
    // Cleared by conversation (an empty save), so it also reaches a copy the server has under another id, and it
    // waits in the queue when offline like any other save.
    const req: SaveDraftRequest = { channelId: d.channelId, text: "", updatedAt: this.ws.deps.clock.now() };
    if (d.threadRootId !== undefined) req.threadRootId = d.threadRootId;
    this.save(req);
  }

  // ---------- Sent ----------

  /** Loads the Sent tab: my latest messages in conversations I can still read. */
  loadSent(): Promise<void> {
    this.update({ sentStatus: "loading" });
    return this.ws.deps.api.searchMessages(this.ws.wsId, "from:@me").then(
      (r) => {
        if (!this.isDisposed) this.update({ sent: [...r.messages].sort((a, b) => b.createdAt - a.createdAt), sentStatus: "ready" });
      },
      () => {
        if (!this.isDisposed) this.update({ sentStatus: "failed", offline: !this.ws.state.network });
      },
    );
  }

  sentRows(): SentRow[] {
    const cache = this.ws.state.cache;
    if (cache === undefined) return [];
    const rows: SentRow[] = [];
    for (const m of this.state.sent ?? []) {
      const c = cache.channels[m.channelId];
      if (c === undefined) continue;
      const isDm = c.kind === "dm" || c.kind === "group_dm";
      const ctx = { meId: cache.meId, members: cache.members, channels: cache.channels, userGroups: cache.userGroups ?? {} };
      rows.push({ id: m.id, message: m, preview: messagePreview(m, ctx, SENT_PREVIEW_MAX), conversation: isDm ? channelTitle(cache, c.id) : `#${c.name}`, isDm, inThread: m.threadRootId !== undefined });
    }
    return rows;
  }

  // ---------- Sync (the composers) ----------

  /** When this device's copy of a conversation's draft was last typed (0 = unknown). */
  localAt(channelId: string, threadRootId?: string): number {
    const n = Number(this.ws.deps.storage.get(this.atKey(draftScope(channelId, threadRootId))) ?? 0);
    return Number.isFinite(n) ? n : 0;
  }

  /** The draft as the server has it for one conversation (its composer or a thread's); undefined = none or unknown. */
  async remote(channelId: string, threadRootId?: string): Promise<Draft | undefined | "unknown"> {
    const list = this.ws.deps.api.listDrafts;
    if (list === undefined) return "unknown";
    try {
      const drafts = await list.call(this.ws.deps.api, this.ws.wsId, channelId);
      return drafts.find((d) => d.threadRootId === threadRootId);
    } catch {
      return "unknown";
    }
  }

  /**
   * The composer's text changed (`at` = when it was typed; default now): stored on this device and saved to the
   * server after a pause. Empty text clears the draft.
   */
  typed(channelId: string, threadRootId: string | undefined, text: string, at = this.ws.deps.clock.now()): void {
    const scope = draftScope(channelId, threadRootId);
    this.ws.deps.storage.set(this.atKey(scope), String(at));
    const req: SaveDraftRequest = { channelId, text, updatedAt: at };
    if (threadRootId !== undefined) req.threadRootId = threadRootId;
    this.latest.set(scope, req);
    this.update({ drafts: applySaved(this.state.drafts, channelId, threadRootId, this.asDraft(req, this.state.drafts)) });
    if (!this.synced) return;
    this.cancelTimer(scope);
    this.timers.set(scope, this.ws.deps.clock.setTimer(DRAFT_SYNC_DELAY_MS, () => {
      this.timers.delete(scope);
      this.flushScope(scope);
    }));
  }

  /** The composer took the server's copy (newer than this device's): remember its time, nothing to save. */
  adopted(channelId: string, threadRootId: string | undefined, at: number): void {
    this.ws.deps.storage.set(this.atKey(draftScope(channelId, threadRootId)), String(at));
  }

  /** The composer is going away: save its draft now instead of after the pause. */
  flush(channelId: string, threadRootId?: string): void {
    const scope = draftScope(channelId, threadRootId);
    if (this.timers.has(scope)) this.flushScope(scope);
  }

  private flushScope(scope: string): void {
    this.cancelTimer(scope);
    const req = this.latest.get(scope);
    if (req === undefined) return;
    this.latest.delete(scope);
    this.save(req);
  }

  private save(req: SaveDraftRequest): void {
    const save = this.ws.deps.api.saveDraft;
    if (save === undefined) return;
    save.call(this.ws.deps.api, this.ws.wsId, req).then(
      (kept) => {
        const scope = draftScope(req.channelId, req.threadRootId);
        const cur = this.pending[scope];
        if (cur !== undefined && cur.updatedAt <= req.updatedAt) {
          const { [scope]: _sent, ...rest } = this.pending;
          this.setPending(rest);
        }
        // Unless the composer typed again meanwhile.
        if (!this.isDisposed && !this.latest.has(scope)) this.update({ drafts: applySaved(this.state.drafts, req.channelId, req.threadRootId, kept) });
      },
      () => this.setPending(queueSave(this.pending, req)),
    );
  }

  /** Sends the saves that waited for the network. */
  async flushPending(): Promise<void> {
    const save = this.ws.deps.api.saveDraft;
    if (save === undefined || this.flushing) return;
    this.flushing = true;
    try {
      for (const [scope, req] of Object.entries(this.pending)) {
        try {
          await save.call(this.ws.deps.api, this.ws.wsId, req);
          if (this.pending[scope] === req) {
            const { [scope]: _sent, ...rest } = this.pending;
            this.setPending(rest);
          }
        } catch {
          return; // still offline (or refused): try again on the next reconnect
        }
      }
    } finally {
      this.flushing = false;
    }
  }

  private setPending(next: Record<string, SaveDraftRequest>): void {
    this.pending = next;
    if (Object.keys(next).length === 0) this.ws.deps.storage.remove(this.pendingKey);
    else this.ws.deps.storage.set(this.pendingKey, JSON.stringify(next));
  }

  private cancelTimer(scope: string): void {
    this.timers.get(scope)?.();
    this.timers.delete(scope);
  }

  /** The list entry for a save that hasn't come back from the server yet (keeps the known id). */
  private asDraft(req: SaveDraftRequest, list: readonly Draft[]): Draft | undefined {
    if (req.text.trim() === "") return undefined;
    const known = list.find((d) => d.channelId === req.channelId && d.threadRootId === req.threadRootId);
    const d: Draft = { id: known?.id ?? `local:${draftScope(req.channelId, req.threadRootId)}`, channelId: req.channelId, text: req.text, updatedAt: req.updatedAt };
    if (req.threadRootId !== undefined) d.threadRootId = req.threadRootId;
    return d;
  }
}

function parsePending(raw: string | undefined): Record<string, SaveDraftRequest> {
  if (raw === undefined) return {};
  try {
    const v: unknown = JSON.parse(raw);
    return v !== null && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, SaveDraftRequest>) : {};
  } catch {
    return {};
  }
}
